/**
 * GroupBySelector - Shared grouping selector for card views
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

export interface GroupByOption<T extends string> {
  value: T;
  label: string;
}

export interface GroupBySelectorProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: GroupByOption<T>[];
  className?: string;
}

export default function GroupBySelector<T extends string>({ value, onChange, options, className }: GroupBySelectorProps<T>): ReactElement {
  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <span className="text-xs text-zinc-500">Group by:</span>
      <div className="inline-flex rounded-md border border-zinc-700 bg-zinc-800/50 p-0.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={value === opt.value}
            className={clsx(
              'focus-visible:ring-aether rounded px-2.5 py-1 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2',
              value === opt.value ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
